import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Cpu, BarChart3, Check } from 'lucide-react';

const products = [
  {
    icon: Cpu,
    label: "Core Platform",
    tagline: "Your AI Chief of Staff",
    description: "Meeting intelligence, autonomous CRM updates, evidence-backed forecasting, deal scoring, coaching, and proactive alerts. Everything your revenue team needs in one platform.",
    features: [
      "Auto-capture meetings, transcribe, extract action items",
      "CRM updates itself from every interaction",
      "Choose your methodology: MEDDIC, BANT, SPICED and more",
      "Coaching insights from call recordings",
    ],
    link: "/product/platform",
  },
  {
    icon: BarChart3,
    label: "Command Center",
    tagline: "The CRO War Room",
    description: "An executive dashboard purpose-built for revenue leaders. Pipeline health scoring, evidence-backed forecasts, deal deep-dives, and weekly digests. Defend your number with data, not opinions.",
    features: [
      "Pipeline health scoring at a glance",
      "Forecast Advisor: query your pipeline in plain language",
      "WOW movement tracking and risk flags",
      "Monday Morning CRO Digest via Slack",
    ],
    link: "/product/command-center",
  },
];

interface ProductShowcaseProps {
  onScrollToDemo: () => void;
}

export const ProductShowcase = ({ onScrollToDemo }: ProductShowcaseProps) => {
  return (
    <section className="w-full py-20 md:py-28 bg-foreground text-primary-foreground">
      <div className="container px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-[0.8fr_1.2fr] gap-6 md:gap-16 items-end mb-14 border-b border-background/20 pb-10"
        >
          <h2 className="text-3xl md:text-5xl font-bold leading-tight">
            The operating record behind the number.
          </h2>
          <p className="text-lg text-background/70 max-w-2xl md:ml-auto leading-relaxed">
            NoQ5 connects every customer interaction to the commercial record, then shows revenue leaders what changed and what needs action.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 border-y border-background/20 max-w-6xl mx-auto">
          {products.map((product, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              viewport={{ once: true }}
              className="group py-8 md:p-10 md:first:border-r border-background/20 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 border border-background/30 flex items-center justify-center text-primary-foreground">
                  <product.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-background/60">{product.label}</p>
                  <h3 className="text-xl font-bold text-primary-foreground">{product.tagline}</h3>
                </div>
              </div>

              <p className="text-sm text-background/70 leading-relaxed mb-7">
                {product.description}
              </p>

              <ul className="space-y-2 mb-6 flex-1">
                {product.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-background/80">
                    <Check className="w-4 h-4 text-primary-foreground flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link
                to={product.link}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-foreground hover:text-background/70 transition-colors group/link"
              >
                Learn more
                <ArrowRight className="w-4 h-4 group-hover/link:translate-x-0.5 transition-transform" />
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="flex justify-center mt-12"
        >
          <Button
            size="lg"
            variant="secondary"
            className="font-bold px-10 py-6 text-base group"
            onClick={onScrollToDemo}
          >
            See it in action
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
